import React, { useEffect, useRef, useState } from 'react';
import cfac22 from 'js/library/utils/cfac22';
import {
  blobToBase64,
  changeDateForMilliSeconds,
  changeDateForMilliSecondsWithType,
  convertToDateDefault,
} from 'js/library/utils/helpers';
import { toast } from 'react-toastify';
import { formatStatus } from './model';

import * as D from 'js/components/DesignSystem/Table/styles';
import { Tooltip } from '@mui/material';
import { FileArrowDown, CheckCircle, FileX, Upload } from 'phosphor-react';

import { GoBackHeader } from '../RelationshipClub/GoBackHeader';
import { HeaderTitle } from '../RelationshipClub/HeaderTitle';
import { MainContainer } from 'js/components/Clubs/clubeTableStyles';
import { ModalValidationList } from './ModalValidationList';
import { SectionFilterAndTable } from './SectionFilterAndTable';
import { SectionGenerateFile } from './SectionGenerateFile';
import { createPartnerFiles } from 'js/library/utils/API/createPartnerFiles';
import { getPartnerFiles } from 'js/library/utils/API/getPartnerFiles';
import { getPartnerFilesType } from 'js/library/utils/API/getPartnerFilesType';
import { ModalCancelFile } from './ModalCancelFile';
import { uploadFiles } from 'js/library/utils/API/apiUploadFiles';
import { validatePartnerFile } from 'js/library/utils/API/validatePartnerFile';
import { ModalProcessFile } from './ModalProcessFile';
import { FilePreviewTable } from './FilePreviewTable';

export function PartnerFilesList() {
  const fileInputRef = useRef(null);
  const partnerIdClube = localStorage.getItem('partnerIdClube');
  const [refreshPage, setRefreshPage] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [fileTypeList, setFileTypeList] = useState([]);
  const [fileTypeSelected, setFileTypeSelected] = useState('');
  const [loadingUpdateFile, setLoadingUpdateFile] = useState(false);
  const [isOpenModalValidation, setIsOpenModalValidation] = useState(false);
  const [fileId, setFileId] = useState(null);
  const [isOpenModalCancelFile, setIsOpenModalCancelFile] = useState(false);
  const [isOpenModalProcessFile, setIsOpenModalProcessFile] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [processingType, setProcessingType] = useState('');
  const [scheduleDate, setScheduleDate] = useState(null);
  const [loadingModal, setLoadingModal] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [type, setType] = useState('');
  const [status, setStatus] = useState('');
  const [parsedData, setParsedData] = useState({ columns: [], rows: [] });
  const [validateInfos, setValidateInfos] = useState({
    fileId: '',
    filename: '',
    fileTypeId: '',
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [filesResponse, typesResponse] = await Promise.all([
          getPartnerFiles(partnerIdClube, type, status),
          getPartnerFilesType(partnerIdClube),
        ]);

        const processedFiles = filesResponse
          .map((file) => ({
            ...file,
            creationDate: convertToDateDefault(file.creationDate),
            status: formatStatus(file.status),
            scheduleDate: file.scheduleDate ? convertToDateDefault(file.scheduleDate) : '-',
          }))
          .sort((a, b) => new Date(b.updateDate) - new Date(a.updateDate));

        setFileList(processedFiles);
        setFileTypeList(typesResponse);
      } catch (e) {
        console.error(e);
        toast.error(e);
      }
    };

    partnerIdClube && fetchData();
  }, [partnerIdClube, refreshPage, type, status]);

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    setSelectedFile(file);
    setParsedData({ columns: [], rows: [] });

    if (file) {
      try {
        const { columns, rows } = await extractFieldsFromFile(file);
        setParsedData({ columns, rows });
      } catch (error) {
        toast.error('Erro ao extrair dados do arquivo.');
      }
    }
  };

  const extractFieldsFromFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const text = reader.result;
        const lines = text.split(/\r?\n/).filter((line) => line.trim() !== '');
        if (lines.length === 0) {
          resolve({ columns: [], rows: [] });
          return;
        }

        const separator = lines[0].includes(';') ? ';' : ',';
        const columns = lines[0].split(separator).map((col) => col.trim());

        const rows = lines.slice(1).map((line) =>
          line.split(separator).reduce((acc, curr, idx) => {
            acc[columns[idx]] = curr.trim();
            return acc;
          }, {})
        );

        resolve({ columns, rows });
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsText(file);
    });
  };

  const handleFileUpdate = async () => {
    try {
      setLoadingUpdateFile(true);
      if (!selectedFile) {
        return toast.warning('É necessário selecionar um arquivo para continuar.');
      }

      const arrayBuffer = await readFileAsArrayBuffer(selectedFile);
      const blob = new Blob([arrayBuffer], { type: selectedFile.type });
      const base64String = await blobToBase64(blob);
      const base64Data = base64String.split(',')[1];
      const fileInfo = {
        mimeType: selectedFile.type,
        fileName: `${fileTypeSelected}_${Date.now()}_${selectedFile.name}`,
        buffer: base64Data,
      };

      const successUpload = await uploadFile(fileInfo);
      if (successUpload) {
        const sanitizedFileName = selectedFile.name
          .replace(/\s+/g, '')
          .replace(/[^a-zA-Z0-9_-]/g, '');

        const payload = {
          filename: fileInfo.fileName,
          partnerId: partnerIdClube,
          path: `partnerFiles/${partnerIdClube}/registered/${sanitizedFileName}`,
          type:
            validateInfos.fileTypeId ||
            fileTypeList.find((type) => type.filePrefix === fileTypeSelected)?.id,
          processingType,
          scheduleDate: changeDateForMilliSecondsWithType(null, scheduleDate),
        };
        processingType !== 'scheduled' && delete payload.scheduleDate;

        const newPartnerFiles = await createPartnerFiles(payload);
        toast.success('Arquivo enviado com sucesso.');

        //abrindo modal de validação caso não seja de processamento automático
        if (processingType !== 'automatic') {
          insertValidateInfos(null, newPartnerFiles.filename, newPartnerFiles.id);
        } else {
          setParsedData({ columns: [], rows: [] });
        }

        // setRefreshPage((prev) => !prev);
        setProcessingType('');
        if (fileInputRef.current) fileInputRef.current.value = null;

        setFileTypeSelected('');
        setInputValue('');
        setSelectedFile(null);
      } else {
        toast.warning('O arquivo não preenche todos os requisitos.');
      }
    } catch (e) {
      toast.error(e);
    } finally {
      setLoadingUpdateFile(false);
    }
  };

  const handleCloseModals = (whichModal) => {
    switch (whichModal) {
      case 'validation':
        setParsedData({ columns: [], rows: [] });
        setIsOpenModalValidation((prev) => !prev);
        break;
      case 'cancelFile':
        setIsOpenModalCancelFile((prev) => !prev);
        break;
      case 'processFile':
        setIsOpenModalProcessFile((prev) => !prev);
        break;
      default:
        break;
    }
  };

  const insertValidateInfos = (fileType, fileName, fileId) => {
    const fileTypeId = fileType
      ? fileTypeList.find((type) => type.filePrefix === fileType)?.id
      : fileTypeList.find((type) => type.filePrefix === fileTypeSelected)?.id;

    setValidateInfos({
      ...validateInfos,
      fileTypeId,
      filename: fileName,
      fileId,
    });

    handleCloseModals('validation');
  };

  const readFileAsArrayBuffer = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsArrayBuffer(file);
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = () => reject(new Error('Erro ao ler o arquivo.'));
    });
  };

  const uploadFile = async (fileInfo) => {
    return await uploadFiles(`partnerFiles/${partnerIdClube}/registered`, fileInfo, false);
  };

  const handleSelected = (event, newValue) => {
    setFileTypeSelected(newValue ? newValue.filePrefix : null);
  };

  const validatePartner = async () => {
    setLoadingModal(true);
    try {
      await validatePartnerFile(validateInfos);

      setRefreshPage((prev) => !prev);
      setParsedData({ columns: [], rows: [] });
      setProcessingType('');
      setFileTypeSelected('');
      setInputValue('');
    } catch (e) {
      toast.error(e);
    } finally {
      setLoadingModal(false);
      handleCloseModals('validation');
    }
  };

  const handleModals = (fileId, whichModal) => {
    setFileId(fileId);
    whichModal === 'cancelFile' && setIsOpenModalCancelFile(true);
    whichModal === 'processFile' && setIsOpenModalProcessFile(true);
  };

  const handleDownload = async (file) => {
    const url = `${cfac22('STORAGE_URL')}${encodeURIComponent(file.path)}?alt=media`;
    const response = await fetch(url);
    const blob = await response.blob();

    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = file.filename || 'arquivo.txt';
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(link.href);
  };

  const componentReceiver = (column, childrens) => ({
    columnComponent: <D.TableTH>Ações</D.TableTH>,
    childrenComponent: childrens && (
      <D.TableTD>
        <span style={{ display: 'flex', gap: '1rem' }}>
          <Tooltip title="Baixar arquivo">
            <FileArrowDown
              size={28}
              color="#06bad0"
              onClick={() => handleDownload(childrens)}
              style={{ cursor: 'pointer' }}
            />
          </Tooltip>
          {childrens.status === 'Registrado' && (
            <Tooltip title="Validar arquivo">
              <button
                onClick={() =>
                  insertValidateInfos(childrens.type, childrens.filename, childrens.id)
                }
                style={{ background: 'transparent', border: 0, cursor: 'pointer' }}
              >
                <CheckCircle size={28} color="#37b24d" />
              </button>
            </Tooltip>
          )}
          {childrens.status === 'Validado' && (
            <Tooltip title="Processar arquivo">
              <button
                onClick={() => handleModals(childrens.id, 'processFile')}
                style={{ background: 'transparent', border: 0, cursor: 'pointer' }}
              >
                <Upload size={28} color="#0097fc" />
              </button>
            </Tooltip>
          )}
          {![
            'Processando',
            'Processado com erro',
            'Processado',
            'Cancelado',
            'Validado com erro',
          ].includes(childrens.status) &&
            childrens.processingType !== 'automatic' && (
              <Tooltip title="Cancelar">
                <button
                  onClick={() => handleModals(childrens.id, 'cancelFile')}
                  style={{ background: 'transparent', border: 0, cursor: 'pointer' }}
                >
                  <FileX size={28} color="#C00" />
                </button>
              </Tooltip>
            )}
        </span>
      </D.TableTD>
    ),
  });

  return (
    <MainContainer style={{ padding: '4rem 32px' }}>
      <GoBackHeader />
      <HeaderTitle pageInfo="Listagem e upload de arquivos" />

      <SectionGenerateFile
        fileInputRef={fileInputRef}
        fileTypeList={fileTypeList.filter((type) => type.active)}
        fileTypeSelected={fileTypeSelected}
        handleFileChange={handleFileChange}
        handleFileUpdate={handleFileUpdate}
        handleSelected={handleSelected}
        loadingUpdateFile={loadingUpdateFile}
        processingType={processingType}
        setProcessingType={setProcessingType}
        scheduleDate={scheduleDate}
        setScheduleDate={setScheduleDate}
        inputValue={inputValue}
        setInputValue={setInputValue}
        selectedFile={selectedFile}
      />

      <FilePreviewTable data={parsedData} />

      <SectionFilterAndTable
        componentReceiver={componentReceiver}
        fileList={fileList}
        fileTypeList={fileTypeList}
        type={type}
        setType={setType}
        status={status}
        setStatus={setStatus}
      />

      <ModalValidationList
        close={() => handleCloseModals('validation')}
        loading={loadingModal}
        isOpen={isOpenModalValidation}
        setRefreshPage={setRefreshPage}
        validateApi={validatePartner}
      />

      <ModalCancelFile
        close={() => handleCloseModals('cancelFile')}
        fileId={fileId}
        isOpen={isOpenModalCancelFile}
        setRefreshPage={setRefreshPage}
      />

      <ModalProcessFile
        close={() => handleCloseModals('processFile')}
        fileId={fileId}
        isOpen={isOpenModalProcessFile}
        setRefreshPage={setRefreshPage}
      />
    </MainContainer>
  );
}

